import { ProblemData, Score } from '@gmm/problem';

export type AnyArray = any[];
export type AnyObject = Record<string | number, any>;
export type Any = any;

export type ID = string | number;

export type WorkType =
  | 'ASSIGNMENT'
  | 'EXAM'
  | 'CORRECTIONS'
  | 'SPIRAL_REVIEW'
  | 'NONE';

export interface Work {
  type: WorkType;
  // TODO settle on type or workType. Rn, we get both, type for NewWork and workType for
  // list of available assignments
  workType?: WorkType;
  defaultAssignment?: boolean;

  name: string;
  date?: string;
  workId?: number | undefined;
  available?: boolean;

  dateAssigned?: string;

  sitId?: number;
  ticId?: number;

  // list of work history uses these for totals
  points?: number;
  required?: number;

  // this pair is for targeted skills on an assignment
  newSkillPoints?: number;
  newSkillRequired?: number;
  assignmentRestoreIds?: number[];

  // this pair is for SR skills on an assignment
  spiralReviewPoints?: number;
  spiralReviewRequired?: number;

  // this pair is for for the original exam mistakes on Corrections
  errorsFixed?: number;
  errorsRequired?: number;

  // this pair is for the extra practice on Corrections
  practicePoints?: number;
  practiceRequired?: number;
}

export const hasExamWorkType = (
  work: Pick<Work, 'type' | 'workType'>
): boolean => {
  return work.type === 'EXAM' || work.workType === 'EXAM';
};

// Type guard for Exam
const isExam = (item: Partial<Work | Exam>): item is Exam => {
  return (item as Exam).testId !== undefined;
};

// For convenience, sometimes we pass around big flattened objects from the server
// that have Work fields alongside other fields. For example, see AttemptSubmitted.
// When those objects are passed to the WorkStore, it calls mergeWork
// or stripWork to extract the Work fields.
export const mergeWork = (
  original: Work | Exam,
  inbound: Partial<Work | Exam>
): Work | Exam => {
  const ret = {} as Work | Exam;

  ret.type = inbound.type ?? original.type;
  ret.workType = inbound.workType ?? original.workType;
  ret.name = inbound.name ?? original.name;
  ret.date = inbound.date ?? original.date;
  ret.workId = inbound.workId ?? original.workId;
  ret.available = inbound.available ?? original.available;
  ret.sitId = inbound.sitId ?? original.sitId;
  ret.ticId = inbound.ticId ?? original.ticId;
  ret.newSkillPoints = inbound.newSkillPoints ?? original.newSkillPoints;
  ret.newSkillRequired = inbound.newSkillRequired ?? original.newSkillRequired;
  ret.assignmentRestoreIds =
    inbound.assignmentRestoreIds ?? original.assignmentRestoreIds;
  ret.spiralReviewPoints =
    inbound.spiralReviewPoints ?? original.spiralReviewPoints;
  ret.spiralReviewRequired =
    inbound.spiralReviewRequired ?? original.spiralReviewRequired;
  ret.errorsFixed = inbound.errorsFixed ?? original.errorsFixed;
  ret.errorsRequired = inbound.errorsRequired ?? original.errorsRequired;
  ret.practicePoints = inbound.practicePoints ?? original.practicePoints;
  ret.practiceRequired = inbound.practiceRequired ?? original.practiceRequired;
  ret.dateAssigned = inbound.dateAssigned ?? original.dateAssigned;
  ret.defaultAssignment =
    inbound.defaultAssignment ?? original.defaultAssignment;

  if (isExam(inbound)) {
    const examRet = ret as Exam;

    examRet.testId = inbound.testId;
    examRet.isFollowUp = inbound.isFollowUp;

    return examRet;
  }

  return ret;
};

export const stripWork = (work: Work): Work => {
  return mergeWork({} as Work, work);
};

// only for two types of work: ASSIGNMENT and CORRECTIONS.
// Yeah, naming is tough. Corrections are things that teachers 'assign'.
export interface Assignment extends Work {
  available?: boolean;
  date: string;
}

export interface Exam extends Work {
  sitId: number;
  testId: number;
  isFollowUp?: boolean;
}

export const NO_WORK: Work = { type: 'NONE', name: 'No Work' };

export interface NewWorkProblems {
  problems: ProblemObjects;
  autoUnlockExamsProblems?: boolean;
  dollarRestoreIds?: ID[];
  selectedRestoreId?: number;
}

export interface NewWorkTagAlong {
  severeError?: string;
}

// The thing that gets built by WebClientBean.sendRestores,
// which is now handled by CurrentWorkService
export type NewWork = Work & NewWorkProblems & NewWorkTagAlong;

export interface ExamDatum {
  name?: string;
  sitId?: number;
  ticId?: number;
  isFollowUp?: boolean;
  hasBeenTurnedIn?: boolean;
}

export interface ShowAssignmentsHistory {
  workHistory: Assignment[];
}

export type Proficiency =
  | 'untried'
  | 'not enough data'
  | 'red'
  | 'yellow'
  | 'green'
  | 'silver'
  | 'gold';

export interface HighScores {
  name: string;
  score: number;
}

export interface ProblemObject {
  skillId?: number;
  daysSince?: number;
  firstTry: boolean;
  followUp?: boolean;
  hasBeenTried: boolean;
  isUnfixedExamCorrection?: boolean;
  lastAttempt?: number;
  lastCorrectDate?: string;
  lastThree?: Score;
  lastFive?: Score;
  lastTen?: Score;
  lvl?: Proficiency;
  myAllTime?: Score;
  notValid?: boolean;
  penalty?: number;
  // problem
  p?: ProblemData;
  rawScore?: number;
  ready?: boolean;
  // How many times has the student looked at this problem, then clicked off
  skips?: number;
  // test number
  tn?: number;
  unseen?: boolean;
  mySkillAccuracy?: string;
  mySkillAttempts?: number;
  problemid?: ID;
  dateLearned?: string;
}

export type ProblemObjects = Record<ID, ProblemObject>;

export interface StatisticsProps {
  mySkillAccuracy: string;
  mySkillAttempts: number | undefined;
  lastThree: string;
  lastFive: string;
  lastTen: string;
  lastCorrectDate: string;
  problemid: ID;
}

export const themeOptions = [
  'green',
  'blue',
  'gray',
  'black',
  'orange',
  'pink',
  'purple',
] as const;

export const DEFAULT_THEME_OPTION = 'green';

// Step 2: Use 'typeof' and 'as const' to create the type
export type ThemeOption = typeof themeOptions[number];

export const themeColors = new Map<ThemeOption, string>([
  ['green', '72,148,68'],
  ['blue', '40,124,156'],
  ['gray', '118,118,118'],
  ['black', '0,0,0'],
  ['orange', '255,172,12'],
  ['pink', '248,28,148'],
  ['purple', '96,4,188'],
]);

export const getThemeColor = (themeColorName: ThemeOption): string => {
  return `rgb(${themeColors.get(themeColorName)})`;
};

export const getThemeColorWithOpacity = (
  themeColorName: ThemeOption,
  opacity: number
): string => {
  return `rgba(${themeColors.get(themeColorName)}, ${opacity})`;
};

export const themeColors2 = new Map<ThemeOption, string>([
  ['green', '0,110,7'],
  ['blue', '22,84,107'],
  ['gray', '118,118,118'], // Note: gray is the same in both maps - gray gradient doesn't look good
  ['black', '100,100,100'],
  ['orange', '214,146,0'],
  ['pink', '227,0,123'],
  ['purple', '74,0,148'],
]);

export const getThemeColor2 = (themeColorName: ThemeOption): string => {
  return `rgb(${themeColors2.get(themeColorName)})`;
};

export const contrastingThemeColors = new Map<ThemeOption, string>([
  ['green', '116,40,129'],
  ['blue', '215,95,76'],
  ['gray', '215,95,76'],
  ['black', '25,152,127'],
  ['orange', '0,114,178'],
  ['pink', '93,168,153'],
  ['purple', '93,168,153'],
]);

export const getContrastingThemeColor = (
  themeColorName: ThemeOption
): string => {
  return `rgb(${contrastingThemeColors.get(themeColorName)})`;
};
